import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  LiveKitRoom,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { UserIcon, Video } from "lucide-react";
import Swal from "sweetalert2";
import { collection, doc, getDoc, onSnapshot, orderBy, query, updateDoc, where } from "firebase/firestore";
import { User } from "../AuthContext";
import {
  addUserToRoom,
  createSceneObject,
  createVirtualAvatar,
  db,
  functions,
} from "../../firebase";
import { getSessionMeetingCollection, type MeetingVideoEngine } from "../../utils/sessionMeeting";
import { isLiveKitOpenTestRoom } from "../../utils/livekitOpenTestRoom";
import Scene from "../Meeting/Scene";
import ActionButtons from "../Meeting/ActionButtons";
import RaisedHandNotices from "../Meeting/RaisedHandNotices";
import { useRaisedHands } from "../Meeting/raisedHand";
import InviteUserModal from "../Meeting/InviteUserModal";
import ParticipantsPanelModal from "../Meeting/ParticipantsPanelModal";
import VirtualAvatarModal from "../Meeting/VirtualAvatarModal";
import SceneObjectModal from "../Meeting/SceneObjectModal";
import AvatarSelector from "../AvatarSelector";
import RoomOnboardingTour from "../Meeting/RoomOnboardingTour";
import ObservatorHandler from "../Meeting/ObservatorHandler";
import LiveKitVideoPanel from "./LiveKitVideoPanel";
import LiveKitPilotTelemetry from "../LiveKitPilot/LiveKitPilotTelemetry";
import { playAccessRequestSound } from "../Meeting/notificationSound";
import { getRoomLayoutMode, type RoomLayoutMode } from "../Meeting/mobileLayout";
import {
  AccessRoomResponseOptions,
  AccessRoomResponseStatus,
  RoomAccessRequest,
  RoomAccessRequestStatus,
  SessionSettingsResponse,
} from "../Meeting/types";
import { useCreditBalance } from "../../hooks/useCreditBalance";

/** Compat React 19 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LiveKitRoomView = LiveKitRoom as unknown as FC<any>;

type TokenResponse = {
  token: string;
  serverUrl: string;
  roomName: string;
};

type LiveKitMeetingRoomProps = {
  user: User | null;
  roomId: string;
  sessionSetting: SessionSettingsResponse | null;
  selectedAvatarId: string | null;
  sessionIdentityKey: string | null;
  forceEngine?: MeetingVideoEngine;
};

const LiveKitMeetingRoom: React.FC<LiveKitMeetingRoomProps> = ({
  user,
  roomId,
  sessionSetting,
  selectedAvatarId,
  sessionIdentityKey,
  forceEngine,
}) => {
  const navigate = useNavigate();
  const sessionMeetingCollection = getSessionMeetingCollection(forceEngine);
  const isBetaRoute = forceEngine === "livekit";
  const isOpenTestRoom = isLiveKitOpenTestRoom(roomId);

  const destroySessionCallable = httpsCallable(functions, "destroySession");
  const releaseSessionAdminCallable = httpsCallable<
    { roomId: string },
    { success: boolean; nextAdminUid?: string | null }
  >(functions, "releaseLiveKitSessionAdmin");
  const cancelRoomAccessRequestFunction = httpsCallable<
    { roomId: string },
    { success: boolean; message: string }
  >(functions, "cancelRoomAccessRequest");

  const [session, setSession] = useState<TokenResponse | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [sessionJoined, setSessionJoined] = useState(false);
  const [isLeavingRoom, setIsLeavingRoom] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024,
  );
  const [isPortrait, setIsPortrait] = useState(() => {
    if (typeof window === "undefined") return false;
    const w = window.innerWidth;
    const h = window.innerHeight;
    return w < 1024 && h > w;
  });
  const [activeTab, setActiveTab] = useState<"scene" | "video">(() =>
    typeof window !== "undefined" && window.innerWidth < 1024 ? "scene" : "video",
  );
  const [layoutMode, setLayoutMode] = useState<RoomLayoutMode>(() =>
    getRoomLayoutMode(window.innerWidth, window.innerHeight),
  );
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isVirtualAvatarModalOpen, setIsVirtualAvatarModalOpen] = useState(false);
  const [isSceneObjectModalOpen, setIsSceneObjectModalOpen] = useState(false);
  const [accessRequests, setAccessRequests] = useState<RoomAccessRequest[]>([]);
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
  const [forceRoomTour, setForceRoomTour] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [liveModerators, setLiveModerators] = useState<string[]>([]);
  const [sessionAdminUid, setSessionAdminUid] = useState<string | null>(null);

  const intentionalLeaveRef = useRef(false);
  const meetingDocExistedRef = useRef(false);
  const hasInitializedRequests = useRef(false);
  const promptedSamePersonIds = useRef<Set<string>>(new Set());
  const previousPendingRequestsCount = useRef(0);
  const hasShownLowCreditAlert = useRef(false);
  const joinedFirestoreRef = useRef(false);

  const isOwner = isOpenTestRoom
    ? false
    : (sessionSetting?.isRoomOwner ?? sessionSetting?.isRoomModerator ?? false);
  const isModerator = isOpenTestRoom
    ? !!user?.uid && sessionAdminUid === user.uid
    : isOwner ||
      (sessionSetting?.isRoomModerator ?? false) ||
      (!!user?.uid && liveModerators.includes(user.uid));
  const { hands: raisedHands, mine: handRaised, busy: handBusy, toggle: toggleHand } = useRaisedHands(roomId, user?.uid);

  const { hoursBalance, isLowBalance } = useCreditBalance(user?.uid, isOwner);

  useEffect(() => {
    if (!roomId || !user?.uid) {
      return;
    }

    let cancelled = false;

    const loadToken = async () => {
      setTokenLoading(true);
      setTokenError(null);
      try {
        const getToken = httpsCallable<{ roomId: string }, TokenResponse>(
          functions,
          "getLiveKitToken",
        );
        const result = await getToken({ roomId });
        if (!cancelled) {
          setSession(result.data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : "Não foi possível conectar à sala.";
          setTokenError(message);
        }
      } finally {
        if (!cancelled) {
          setTokenLoading(false);
        }
      }
    };

    void loadToken();

    return () => {
      cancelled = true;
    };
  }, [roomId, user?.uid]);

  const checkOrientation = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const mobile = width < 1024;
    setIsMobile(mobile);
    setIsPortrait(mobile && height > width);
    setLayoutMode(getRoomLayoutMode(width, height));
  }, []);

  useEffect(() => {
    const handleOrientationChange = () => {
      window.setTimeout(checkOrientation, 100);
    };

    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [checkOrientation]);

  useEffect(() => {
    if (!roomId) {
      return;
    }
    const roomRef = doc(db, "rooms", roomId);
    const unsubRoom = onSnapshot(roomRef, (snap) => {
      const mods = snap.data()?.moderators;
      setLiveModerators(Array.isArray(mods) ? mods : []);
    });
    return () => unsubRoom();
  }, [roomId]);

  useEffect(() => {
    if (!roomId || !isOpenTestRoom) {
      setSessionAdminUid(null);
      return;
    }
    const meetingRef = doc(db, sessionMeetingCollection, roomId);
    const unsubMeeting = onSnapshot(meetingRef, (snap) => {
      setSessionAdminUid((snap.data()?.sessionAdminUid as string | undefined) ?? null);
    });
    return () => unsubMeeting();
  }, [roomId, sessionMeetingCollection, isOpenTestRoom]);

  useEffect(() => {
    const meetingRef = doc(db, sessionMeetingCollection, roomId);
    const unsubscribe = onSnapshot(meetingRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        meetingDocExistedRef.current = true;
        return;
      }

      if (!meetingDocExistedRef.current || intentionalLeaveRef.current) {
        return;
      }

      if (!isOwner) {
        Swal.fire({
          icon: "info",
          title: "Sessão encerrada",
          text: "Esta sessão foi encerrada pelo terapeuta. Você será redirecionado.",
        }).then(() => {
          navigate("/");
        });
      }
    });

    return () => unsubscribe();
  }, [navigate, roomId, isOwner, sessionMeetingCollection]);

  const registerFirestoreParticipant = useCallback(async () => {
    if (!user || !roomId || joinedFirestoreRef.current) {
      return;
    }
    joinedFirestoreRef.current = true;
    await addUserToRoom(
      roomId,
      user,
      sessionSetting?.sessionRole || "participant",
      selectedAvatarId,
      sessionIdentityKey,
    );

    const participantRef = doc(db, "rooms", roomId, "participants", user.uid);
    const current = await getDoc(participantRef);
    const currentData = current.data();
    const alreadyChosen =
      currentData?.roleInSession === "representative" && !!currentData?.avatarId;
    const avatarToApply = selectedAvatarId || user.avatar || null;
    if (!alreadyChosen && avatarToApply) {
      await updateDoc(participantRef, {
        avatarId: avatarToApply,
        roleInSession: "representative",
        promotionStatus: "accepted",
      });
    }

    setSessionJoined(true);
  }, [roomId, user, sessionSetting?.sessionRole, selectedAvatarId, sessionIdentityKey]);

  const handleAvatarPicked = useCallback(
    async (avatarId: string | null) => {
      if (!user || !roomId) {
        setShowAvatarPicker(false);
        return;
      }

      if (!avatarId) {
        setShowAvatarPicker(false);
        return;
      }

      try {
        const participantRef = doc(db, "rooms", roomId, "participants", user.uid);
        await updateDoc(participantRef, {
          avatarId,
          roleInSession: "representative",
          promotionStatus: "accepted",
        });
        setShowAvatarPicker(false);
      } catch (error) {
        console.error("Erro ao escolher avatar:", error);
        Swal.fire({
          icon: "error",
          title: "Erro ao escolher avatar",
          text: "Não foi possível aplicar o avatar. Tente novamente.",
        });
      }
    },
    [roomId, user],
  );

  useEffect(() => {
    if (!isModerator || !sessionJoined) {
      setAccessRequests([]);
      previousPendingRequestsCount.current = 0;
      hasInitializedRequests.current = false;
      return;
    }

    const requestsRef = collection(db, sessionMeetingCollection, roomId, "accessRequests");
    const pendingQuery = query(
      requestsRef,
      where("status", "==", "pending"),
      orderBy("requestedAt", "asc"),
    );

    const unsubscribe = onSnapshot(pendingQuery, (snapshot) => {
      const pendingRequests: RoomAccessRequest[] = snapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data() as Record<string, unknown>;
        const requestedAt = data.requestedAt as { toDate?: () => Date } | undefined;
        return {
          id: docSnapshot.id,
          userId: data.userId as string,
          status: (data.status as RoomAccessRequestStatus) ?? AccessRoomResponseStatus.PENDING,
          name: (data.name as string | null) ?? null,
          email: (data.email as string | null) ?? null,
          sessionIdentityKey: (data.sessionIdentityKey as string | null) ?? null,
          samePerson: data.samePerson === true,
          requestedAt: requestedAt?.toDate ? requestedAt.toDate() : null,
        };
      });

      const dedupedByIdentity = new Map<string, RoomAccessRequest>();
      for (const request of pendingRequests) {
        const identityKey = request.samePerson
          ? request.userId
          : (
          request.sessionIdentityKey ||
          request.email ||
          request.name ||
          request.userId
        )
          .toString()
          .trim()
          .toLowerCase();

        const existing = dedupedByIdentity.get(identityKey);
        if (!existing) {
          dedupedByIdentity.set(identityKey, request);
          continue;
        }

        const existingTime = existing.requestedAt?.getTime?.() ?? 0;
        const currentTime = request.requestedAt?.getTime?.() ?? 0;
        if (currentTime >= existingTime) {
          dedupedByIdentity.set(identityKey, request);
        }
      }

      const uniqueRequests = Array.from(dedupedByIdentity.values()).sort(
        (a, b) => (a.requestedAt?.getTime?.() ?? 0) - (b.requestedAt?.getTime?.() ?? 0),
      );

      setAccessRequests(uniqueRequests);
    });

    return () => unsubscribe();
  }, [roomId, isModerator, sessionJoined, sessionMeetingCollection]);

  useEffect(() => {
    if (!isModerator) {
      return;
    }

    if (!hasInitializedRequests.current) {
      hasInitializedRequests.current = true;
      previousPendingRequestsCount.current = accessRequests.length;
      return;
    }

    if (accessRequests.length > previousPendingRequestsCount.current) {
      playAccessRequestSound();
    }

    previousPendingRequestsCount.current = accessRequests.length;
  }, [accessRequests, isModerator]);

  useEffect(() => {
    if (!isOwner || !isLowBalance || hasShownLowCreditAlert.current) {
      return;
    }
    hasShownLowCreditAlert.current = true;
    Swal.fire({
      icon: "warning",
      title: "Créditos baixos",
      text: `Você tem apenas ${hoursBalance}h de crédito restante. Considere adquirir mais créditos.`,
    });
  }, [isLowBalance, hoursBalance, isOwner]);

  const handleSceneError = useCallback((error: string) => {
    Swal.fire({
      icon: "error",
      title: "Erro na sala",
      text: error,
    });
  }, []);

  const handleModeratorLeave = useCallback(async () => {
    await destroySessionCallable(roomId);
  }, [destroySessionCallable, roomId]);

  const handleLeaveMeeting = useCallback(async () => {
    if (isLeavingRoom) {
      return;
    }

    if (isOwner) {
      const confirmResult = await Swal.fire({
        icon: "warning",
        title: "Encerrar a sessão para todos?",
        html:
          "Você é o <strong>moderador</strong> desta sala. Ao encerrar, " +
          "todos os participantes serão desconectados.<br/><br/>" +
          "Se preferir continuar, a sala permanece aberta.",
        showCancelButton: true,
        confirmButtonText: "Sim, encerrar sessão",
        cancelButtonText: "Não, continuar na sala",
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#6b7280",
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      setIsLeavingRoom(true);
      try {
        await handleModeratorLeave();
      } catch (error) {
        console.error("Erro ao encerrar a sessão:", error);
        setIsLeavingRoom(false);
        await Swal.fire({
          icon: "error",
          title: "Não foi possível encerrar",
          text:
            "A sala continua aberta. Verifique sua conexão e toque " +
            "em Sair novamente para encerrar a sessão.",
        });
        return;
      }

      intentionalLeaveRef.current = true;
      window.location.href = "/painel";
      return;
    }

    intentionalLeaveRef.current = true;
    setIsLeavingRoom(true);

    try {
      if (isOpenTestRoom && user?.uid === sessionAdminUid) {
        await releaseSessionAdminCallable({ roomId });
      }
      if (user && roomId) {
        const markIntentionalRoomLeave = httpsCallable<{ roomId: string }, { success: boolean }>(
          functions,
          "markIntentionalRoomLeave",
        );
        await Promise.all([
          cancelRoomAccessRequestFunction({ roomId }),
          markIntentionalRoomLeave({ roomId }),
        ]);
      }
    } catch (error) {
      console.error("Erro ao sair da reunião:", error);
    } finally {
      window.location.href = isOpenTestRoom ? "/" : "/painel";
    }
  }, [
    cancelRoomAccessRequestFunction,
    handleModeratorLeave,
    isLeavingRoom,
    isOpenTestRoom,
    isOwner,
    releaseSessionAdminCallable,
    roomId,
    sessionAdminUid,
    user,
  ]);

  const handleCreateVirtualAvatar = useCallback(
    async ({ name, avatarId }: { name: string; avatarId: string }) => {
      if (!roomId || !user || !isModerator) {
        return;
      }

      try {
        await createVirtualAvatar(roomId, {
          name,
          avatarId,
          createdBy: user.uid,
          createdByName: user.name || user.email || null,
        });
        setIsVirtualAvatarModalOpen(false);
      } catch (error) {
        console.error("Erro ao criar avatar virtual:", error);
        Swal.fire({
          icon: "error",
          title: "Erro ao adicionar avatar",
          text: "Não foi possível criar o avatar virtual. Tente novamente.",
        });
      }
    },
    [roomId, user, isModerator],
  );

  const handleCreateSceneObject = useCallback(
    async ({ name, objectId }: { name: string; objectId: string }) => {
      if (!roomId || !user || !isModerator) {
        return;
      }

      try {
        await createSceneObject(roomId, {
          name,
          objectId,
          createdBy: user.uid,
          createdByName: user.name || user.email || null,
        });
        setIsSceneObjectModalOpen(false);
      } catch (error) {
        console.error("Erro ao criar objeto:", error);
        Swal.fire({
          icon: "error",
          title: "Erro ao adicionar objeto",
          text: "Não foi possível criar o objeto. Tente novamente.",
        });
      }
    },
    [roomId, user, isModerator],
  );

  const handleRespondToAccessRequest = useCallback(
    async (
      requestId: string,
      action: AccessRoomResponseOptions.APPROVE | AccessRoomResponseOptions.DENY,
      samePerson?: boolean,
    ) => {
      if (!isModerator) {
        return;
      }

      setProcessingRequestId(requestId);
      try {
        const respond = httpsCallable<
          {
            roomId: string;
            requestId: string;
            action: AccessRoomResponseOptions.APPROVE | AccessRoomResponseOptions.DENY;
            samePerson?: boolean;
          },
          { success: boolean; message?: string; offerAdminRestore?: boolean; userName?: string | null; requestUserId?: string }
        >(functions, "respondToRoomAccessRequest");

        const result = await respond({ roomId, requestId, action, samePerson });

        if (action === AccessRoomResponseOptions.APPROVE && result.data.offerAdminRestore) {
          const personName = result.data.userName || "Este usuário";
          const choice = await Swal.fire({
            icon: "question",
            title: "Manter como administrador?",
            text: `${personName} pediu para entrar. Manter os poderes de administrador?`,
            showCancelButton: true,
            confirmButtonText: "Sim",
            cancelButtonText: "Não, entra como participante",
            allowOutsideClick: false,
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6b7280",
          });
          const answer = httpsCallable<
            { roomId: string; targetUserId: string; keep: boolean },
            { success: boolean }
          >(functions, "answerAdminRestore");
          await answer({
            roomId,
            targetUserId: result.data.requestUserId || requestId,
            keep: choice.isConfirmed,
          });
        }
      } catch (error) {
        console.error("Erro ao responder solicitação de acesso:", error);
        Swal.fire({
          icon: "error",
          title: "Não foi possível processar o pedido",
          text: "Tente novamente em instantes.",
        });
      } finally {
        setProcessingRequestId(null);
      }
    },
    [isModerator, roomId],
  );

  useEffect(() => {
    if (!isModerator) return;
    const pending = accessRequests.find(
      (request) => request.samePerson && !promptedSamePersonIds.current.has(request.id),
    );
    if (!pending) return;
    promptedSamePersonIds.current.add(pending.id);
    const personName = pending.name || "Esta pessoa";
    void Swal.fire({
      icon: "question",
      title: personName,
      showCancelButton: true,
      confirmButtonText: "Sim, é a mesma",
      cancelButtonText: "Não, é outra pessoa",
      allowOutsideClick: false,
      confirmButtonColor: "#0f766e",
      cancelButtonColor: "#6b7280",
    }).then((choice) => {
      void handleRespondToAccessRequest(
        pending.id,
        AccessRoomResponseOptions.APPROVE,
        choice.isConfirmed,
      );
    });
  }, [accessRequests, handleRespondToAccessRequest, isModerator]);

  const handleSetModerator = useCallback(
    async (targetUserId: string, makeModerator: boolean) => {
      if (!isModerator || !roomId) {
        return;
      }
      try {
        const setMod = httpsCallable<
          { roomId: string; targetUserId: string; makeModerator: boolean },
          { success: boolean; message?: string }
        >(functions, "setRoomModerator");
        await setMod({ roomId, targetUserId, makeModerator });
      } catch (error) {
        console.error("Erro ao atualizar administrador da sala:", error);
        Swal.fire({
          icon: "error",
          title: "Não foi possível atualizar",
          text: "Não foi possível alterar o administrador. Tente novamente.",
        });
      }
    },
    [isModerator, roomId],
  );

  const handleKickUser = useCallback(
    async (userIdToKick: string) => {
      if (!sessionSetting?.isRoomModerator || !roomId || !user || userIdToKick === user.uid) {
        return;
      }

      const kick = httpsCallable<
        { roomId: string; participantIdentity: string },
        { success: boolean }
      >(functions, "kickLiveKitParticipant");

      await kick({ roomId, participantIdentity: userIdToKick });
    },
    [roomId, sessionSetting?.isRoomModerator, user],
  );

  const handleConnected = useCallback(() => {
    void registerFirestoreParticipant();
  }, [registerFirestoreParticipant]);

  const mobileTabButtonClass = (tab: "scene" | "video") =>
    `flex-1 py-2 px-2 text-xs font-medium flex items-center justify-center space-x-2 transition-colors ${
      activeTab === tab ? "bg-blue-600 text-white" : "text-gray-300 hover:bg-gray-700"
    }`;

  if (tokenLoading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-slate-900 text-white gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
        <p>Conectando à sala…</p>
      </div>
    );
  }

  if (tokenError || !session) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-slate-900 text-white px-6 text-center gap-4">
        <p className="text-red-300">{tokenError || "Sessão indisponível."}</p>
        <button
          type="button"
          onClick={() => navigate("/painel")}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium hover:bg-blue-700"
        >
          Voltar ao painel
        </button>
      </div>
    );
  }

  return (
    <div className="meeting-room-shell bg-slate-900 flex flex-col overflow-hidden">
      <RaisedHandNotices hands={raisedHands} />
      {isBetaRoute && (
        <div className="flex-shrink-0 border-b border-amber-500/40 bg-amber-950/80 px-4 py-1.5 text-center text-xs text-amber-100">
          {isOpenTestRoom ? (
            <>
              <span className="font-semibold">TESTE ABERTO LiveKit</span>
              <span className="ml-2 opacity-80">
                Entrada livre — primeiro vira admin
                {sessionAdminUid ? ` · admin: ${sessionAdminUid.slice(0, 8)}…` : " · sem admin"}
              </span>
            </>
          ) : (
            <>
              <span className="font-semibold">BETA LiveKit</span>
              <span className="ml-2 opacity-80">Cenário + avatares + vídeo (teste — produção em /sala/ continua Cloudflare)</span>
            </>
          )}
        </div>
      )}
      <LiveKitRoomView
        token={session.token}
        serverUrl={session.serverUrl}
        connect
        audio
        video
        onConnected={handleConnected}
        data-lk-theme="default"
        className="livekit-room-shell flex flex-1 min-h-0 w-full flex-col overflow-hidden"
      >
      <LiveKitPilotTelemetry roomId={roomId} userId={user?.uid ?? null} />
      <RoomOnboardingTour
        isModerator={isModerator}
        isMobile={isMobile}
        forceOpen={forceRoomTour}
        onForceOpenHandled={() => setForceRoomTour(false)}
        onActivateTab={(tab) => setActiveTab(tab)}
      />
      <ObservatorHandler roomId={roomId} user={user} />
      <InviteUserModal
        showInviteModal={showInviteModal}
        setShowInviteModal={setShowInviteModal}
        roomId={roomId}
        user={user}
      />
      <ParticipantsPanelModal
        meetingClient={null}
        roomId={roomId}
        user={user}
        sessionSetting={{ isRoomModerator: isModerator }}
        isOwner={isOwner}
        moderators={liveModerators}
        onSetModerator={handleSetModerator}
        isPanelOpen={isPanelOpen}
        setIsPanelOpen={setIsPanelOpen}
        accessRequests={accessRequests}
        onRespondToAccessRequest={handleRespondToAccessRequest}
        processingRequestId={processingRequestId}
        onKickUser={handleKickUser}
      />
      <VirtualAvatarModal
        isOpen={isVirtualAvatarModalOpen}
        onClose={() => setIsVirtualAvatarModalOpen(false)}
        onConfirm={handleCreateVirtualAvatar}
      />
      <SceneObjectModal
        isOpen={isSceneObjectModalOpen}
        onClose={() => setIsSceneObjectModalOpen(false)}
        onConfirm={handleCreateSceneObject}
      />
      {showAvatarPicker && (
        <AvatarSelector
          onConfirm={handleAvatarPicked}
          onClose={() => setShowAvatarPicker(false)}
          isModal={true}
          forceAvatarSelection={true}
        />
      )}

      <div className="flex flex-1 min-h-0 w-full">
        {isMobile ? (
          <div className="flex-1 flex flex-col min-h-0 w-full">
            {!isPortrait && (
              <header className="flex-shrink-0 bg-gray-800/95 backdrop-blur-md border-b border-gray-700/50 z-10 flex">
                <button
                  type="button"
                  onClick={() => setActiveTab("scene")}
                  className={mobileTabButtonClass("scene")}
                >
                  <UserIcon className="w-4 h-4" /> <span>Cenário</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("video")}
                  className={mobileTabButtonClass("video")}
                >
                  <Video className="w-4 h-4" /> <span>Videochamada</span>
                </button>
              </header>
            )}

            <main className="flex-1 min-h-0 relative">
              {isPortrait && (
                <div className="absolute top-[calc(0.5rem+env(safe-area-inset-top,0px))] left-3 right-3 z-30 flex rounded-full bg-gray-900/85 backdrop-blur-md border border-gray-700/50 p-0.5 shadow-lg">
                  <button
                    type="button"
                    onClick={() => setActiveTab("scene")}
                    className={`${mobileTabButtonClass("scene")} rounded-full`}
                  >
                    <UserIcon className="w-4 h-4" /> <span>Cenário</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("video")}
                    className={`${mobileTabButtonClass("video")} rounded-full`}
                  >
                    <Video className="w-4 h-4" /> <span>Videochamada</span>
                  </button>
                </div>
              )}

              {isPortrait && activeTab === "scene" && (
                <button
                  type="button"
                  onClick={() => setActiveTab("video")}
                  className="absolute bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] left-3 z-30 flex items-center gap-1.5 rounded-full bg-gray-900/85 px-3 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur-sm border border-gray-700/50"
                  aria-label="Abrir videochamada"
                >
                  <Video className="h-4 w-4 text-blue-400" />
                  Videochamada
                </button>
              )}

              <div
                data-tour="scene"
                className={`absolute inset-0 h-full w-full ${activeTab === "scene" ? "block" : "hidden"}`}
              >
                <Scene
                  roomId={roomId}
                  onError={handleSceneError}
                  sessionSetting={
                    sessionSetting
                      ? {
                          ...sessionSetting,
                          isRoomModerator: isModerator,
                          isRoomOwner: isOwner,
                        }
                      : sessionSetting
                  }
                  layoutMode={layoutMode}
                />
              </div>
              <div
                data-tour="video"
                className={`absolute inset-0 h-full w-full ${activeTab === "video" ? "block" : "hidden"}`}
              >
                <LiveKitVideoPanel roomId={roomId} raisedHandUids={raisedHands.map((hand) => hand.uid)} />
              </div>
            </main>

            <ActionButtons
              isModerator={isModerator}
              isMobile={true}
              roomId={roomId}
              user={user}
              isLeavingRoom={isLeavingRoom}
              isMeetingLoaded={sessionJoined}
              setShowInviteModal={setShowInviteModal}
              setIsPanelOpen={setIsPanelOpen}
              handleLeaveMeeting={handleLeaveMeeting}
              onAddVirtualAvatar={() => setIsVirtualAvatarModalOpen(true)}
              onAddSceneObject={() => setIsSceneObjectModalOpen(true)}
              pendingApprovalsCount={isModerator ? accessRequests.length : 0}
              onShowRoomTour={() => setForceRoomTour(true)}
              handRaised={handRaised}
              handBusy={handBusy}
              onToggleHand={() => { void toggleHand(); }}
              isPortrait={isPortrait}
              activeMobileTab={activeTab}
              onPickAvatar={() => setShowAvatarPicker(true)}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex relative w-full h-full">
            <main className="flex-1 flex min-h-0 h-full pb-20">
              <div data-tour="scene" className="flex-1 relative min-w-0 min-h-0">
                <div className="absolute inset-0">
                  <Scene
                    roomId={roomId}
                    onError={handleSceneError}
                    sessionSetting={
                      sessionSetting
                        ? {
                            ...sessionSetting,
                            isRoomModerator: isModerator,
                            isRoomOwner: isOwner,
                          }
                        : sessionSetting
                    }
                    layoutMode="desktop"
                  />
                </div>
              </div>

              <aside
                data-tour="video"
                className="w-80 min-h-0 self-stretch bg-gray-800/95 backdrop-blur-md shadow-2xl border-l border-gray-700/50 flex flex-col"
              >
                <header className="p-3 border-b border-gray-700/50">
                  <h3 className="text-base font-semibold text-gray-100 flex items-center space-x-2">
                    <Video className="w-4 h-4 text-blue-500" />
                    <span>Videochamada</span>
                  </h3>
                </header>
                <div className="relative flex-1 min-h-0">
                  <LiveKitVideoPanel roomId={roomId} raisedHandUids={raisedHands.map((hand) => hand.uid)} />
                </div>
              </aside>
            </main>
            <footer data-tour="actions" className="absolute bottom-0 left-0 right-0 z-[60]">
              <div className="px-4 py-3 flex justify-center">
                <ActionButtons
                  isModerator={isModerator}
                  isMobile={false}
                  roomId={roomId}
                  user={user}
                  isLeavingRoom={isLeavingRoom}
                  isMeetingLoaded={sessionJoined}
                  setShowInviteModal={setShowInviteModal}
                  setIsPanelOpen={setIsPanelOpen}
                  handleLeaveMeeting={handleLeaveMeeting}
                  onAddVirtualAvatar={() => setIsVirtualAvatarModalOpen(true)}
                  onAddSceneObject={() => setIsSceneObjectModalOpen(true)}
                  pendingApprovalsCount={isModerator ? accessRequests.length : 0}
                  onShowRoomTour={() => setForceRoomTour(true)}
                  handRaised={handRaised}
                  handBusy={handBusy}
                  onToggleHand={() => { void toggleHand(); }}
                  onPickAvatar={() => setShowAvatarPicker(true)}
                />
              </div>
            </footer>
          </div>
        )}
      </div>
      </LiveKitRoomView>
    </div>
  );
};

export default LiveKitMeetingRoom;
